import { Add, Api, Campaign, Create, Delete, Description, Edit, Email, Flag, Search } from "@mui/icons-material";
import { Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DurationInput from "../DurationInput";
import EventTypeSelect from "../EventTypeSelect";
import FieldMapper from "../FieldMapper";
import FlussConditionBuilder from "../FlussConditionBuilder";
import QueueSelect from "../QueueSelect";
import DataPicker from "../DataPicker";
import TemplateTextField from "../TemplateTextField";
import { DurationValue, FieldMapping, FlussValue, QueueSelection, ScopeOption } from "../fluss-builder-types";
import FindRecordFilterField, { findRecordFieldLabel } from "./FindRecordFilterField";
import FlussNodePanelField, { useFlussFieldPath } from "./FlussNodePanelField";
import { FlussMetadataOption, FlussNodePanelData } from "./fluss-node-panel-data";
import { metadataToCreatableScopeOptions, metadataToScopeOptions, sortFactoryActions } from "./fluss-node-panel-utils";
import { useFlussTableColumns } from "./useFlussNodePanelMetadata";
import { renderTableOption, sortedTableOptions } from "./TableOption";

export interface FactoryParamSchema {
    key: string;
    label?: string;
    input_type: "table" | "field" | "scope" | "condition" | "duration" | "text" | "select" | "event_type" | "email_template" | "user_group";
    required?: boolean;
    options?: Record<string, any>;
    // PRIM-015: explicit create/update discriminator for "scope" + multiple (FieldMapper) params.
    // Absent (or true) keeps today's behavior -- required destination fields are pre-populated
    // and locked, which is correct when the mapping creates a record. An action whose mapping
    // targets a record that already exists (e.g. update_record) sets this to false so its
    // required columns are neither forced in nor undeletable. Deliberately a field on the
    // schema itself, not inferred from the action's name/id.
    enforce_required?: boolean;
}

const ICONS: Record<string, React.ElementType> = {
    IconDatabasePlus: Create,
    IconEdit: Edit,
    IconMail: Email,
    IconApi: Api,
    IconBroadcast: Campaign,
    IconFlag: Flag,
    IconFileText: Description,
    IconSearch: Search,
};

export const queueSelectionFromFactoryUserGroup = (value: unknown): QueueSelection => {
    if (!value) return null;
    if (typeof value === "string") {
        return /^(record|var)\.|^@property:/.test(value)
            ? { mode: "record_person", source: value }
            : { mode: "fixed_user", userId: value };
    }
    if (typeof value !== "object" || Array.isArray(value)) return null;
    const raw = value as Record<string, unknown>;
    if (typeof raw.assigneeRole === "string" && raw.assigneeRole) return { mode: "queue", roleName: raw.assigneeRole };
    if (typeof raw.assigneeUser === "string" && raw.assigneeUser) return { mode: "fixed_user", userId: raw.assigneeUser };
    if (typeof raw.assigneeExpression === "string" && raw.assigneeExpression) return { mode: "record_person", source: raw.assigneeExpression };
    return null;
};

export const factoryUserGroupFromQueueSelection = (value: QueueSelection): Record<string, string> | undefined => {
    if (value?.mode === "queue" && value.roleName) return { assigneeRole: value.roleName };
    if (value?.mode === "fixed_user" && value.userId) return { assigneeUser: value.userId };
    if (value?.mode === "record_person" && typeof value.source === "string" && value.source) return { assigneeExpression: value.source };
    return undefined;
};

export const parseFactoryParams = (raw: unknown): Record<string, any> => {
    if (!raw) return {};
    try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (_error) {
        return {};
    }
};

export const parseFactorySchema = (raw: unknown): FactoryParamSchema[] => {
    try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
        return [];
    }
};

export const FactoryActionCards = ({
    actions,
    selectedId,
    disabled,
    onSelect,
}: {
    actions: FlussMetadataOption[];
    selectedId: string;
    disabled?: boolean;
    onSelect: (action: FlussMetadataOption) => void;
}) => {
    const { t } = useTranslation();
    return (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
            {sortFactoryActions(actions.filter((action) => action.active !== false)).map((action) => {
                const Icon = ICONS[action.icon || ""] || Create;
                const selected = action.id === selectedId;
                return (
                    <Paper
                        key={action.id}
                        component="button"
                        type="button"
                        disabled={disabled}
                        data-testid={`factory-action-${action.name}`}
                        aria-pressed={selected}
                        onClick={() => onSelect(action)}
                        sx={{
                            minHeight: 82,
                            p: 1.25,
                            border: 2,
                            borderColor: selected ? "primary.main" : "divider",
                            borderRadius: 1.5,
                            bgcolor: selected ? "action.selected" : "background.paper",
                            color: "text.primary",
                            textAlign: "left",
                            cursor: disabled ? "default" : "pointer",
                        }}
                    >
                        <Stack spacing={0.5} alignItems="flex-start">
                            <Icon color={selected ? "primary" : "inherit"} fontSize="small" />
                            <Typography variant="body2" fontWeight={700}>
                                {t(action.label || `fluss.node_panel.service.action.${action.name}`, { defaultValue: action.name.replaceAll("_", " ").replace(/^./, (letter) => letter.toUpperCase()) })}
                            </Typography>
                        </Stack>
                    </Paper>
                );
            })}
        </Box>
    );
};

const mappingRows = (value: unknown): FieldMapping[] => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return [];
    return Object.entries(value).map(([target, source]) => ({ target, source: source as FlussValue }));
};

const freeKeyMappingRows = (value: unknown): FieldMapping[] => {
    if (typeof value !== "string") return mappingRows(value);
    try {
        return mappingRows(JSON.parse(value));
    } catch (_error) {
        return [];
    }
};
const rowsObject = (rows: FieldMapping[]) => Object.fromEntries(
    rows.filter((row) => row.target && row.source !== undefined).map((row) => [row.target, row.source])
);
const simplePayloadRows = (text: string): { ok: true; rows: FieldMapping[] } | { ok: false } => {
    try {
        const parsed = JSON.parse(text);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return { ok: false };
        const rows = Object.entries(parsed);
        if (rows.some(([, value]) => value !== null && typeof value === "object")) return { ok: false };
        return { ok: true, rows: rows.map(([target, source]) => ({ target, source: source as FlussValue })) };
    } catch (_error) {
        return { ok: false };
    }
};
const DATA_FIELD_KEY = /^[a-z_][a-zA-Z0-9_]*$/;
const HTTP_HEADER_KEY = /^[A-Za-z0-9!#$%&\x27*+.^_\x60|~-]+$/;
const RESERVED_DATA_FIELD_KEYS = new Set(["record", "data", "value", "task", "instance", "content"]);
const emptyOrderValue = (value: unknown) => value === undefined || value === null || value === ""
    || (Array.isArray(value) && value.length === 0)
    || (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0);

// PRIM-016: "+ Field" used to feed FieldMapper a `value` derived straight from the serialized
// params (`mappingRows(value)`). rowsObject correctly drops incomplete rows when persisting, but
// with the FieldMapper's own `value` coming from that same filtered round-trip, a freshly added
// blank row (no target/source yet) never survived the re-render its own onChange triggered -- it
// looked like the button only "saved" without opening a row. Every other panel avoids this by
// keeping the in-progress rows in local component state (see UserTaskNodePanel in
// ActivityNodePanels.tsx) and only reflecting the filtered/complete rows into params; this field
// now follows the same pattern instead of round-tripping through the serialized value.
const ScopeMappingField = ({
    value,
    onChange,
    targetFields,
    disabled,
    enforceRequired,
    templateText,
}: {
    value: unknown;
    onChange: (rows: FieldMapping[]) => void;
    targetFields: ScopeOption[];
    disabled?: boolean;
    enforceRequired?: boolean;
    templateText?: boolean;
}) => {
    const [rows, setRows] = useState<FieldMapping[]>(() => mappingRows(value));
    return (
        <FieldMapper
            value={rows}
            onChange={(next) => {
                setRows(next);
                onChange(next);
            }}
            targetFields={targetFields}
            disabled={disabled}
            enforceRequired={enforceRequired}
            templateText={templateText}
        />
    );
};

const FreeKeyScopeMappingField = ({
    value,
    onChange,
    disabled,
    keyFormat,
}: {
    value: unknown;
    onChange: (rows: FieldMapping[]) => void;
    disabled?: boolean;
    keyFormat?: "http_header" | "json_property";
}) => {
    const { t } = useTranslation();
    const fieldPath = useFlussFieldPath();
    const [rows, setRows] = useState<FieldMapping[]>(() => freeKeyMappingRows(value));
    const validateKey = keyFormat === "http_header"
        ? (key: string) => HTTP_HEADER_KEY.test(key)
        : keyFormat === "json_property"
            ? (key: string) => key.trim().length > 0
            : (key: string) => DATA_FIELD_KEY.test(key) && !RESERVED_DATA_FIELD_KEYS.has(key);
    const keyLabel = keyFormat === "http_header"
        ? String(t("fluss.node_panel.factory_action.header_key", { defaultValue: "Header name" }))
        : keyFormat === "json_property"
            ? String(t("fluss.node_panel.factory_action.payload_key", { defaultValue: "Payload key" }))
            : String(t("fluss.node_panel.factory_action.template_data_key", { defaultValue: "Template data key" }));
    const valueLabel = keyFormat === "http_header"
        ? String(t("fluss.node_panel.factory_action.header_value", { defaultValue: "Header value" }))
        : keyFormat === "json_property"
            ? String(t("fluss.node_panel.factory_action.payload_value", { defaultValue: "Payload value" }))
            : String(t("fluss.node_panel.factory_action.template_data_value", { defaultValue: "Template data value" }));
    const commit = (next: FieldMapping[]) => {
        setRows(next);
        onChange(next);
    };
    const updateRow = (index: number, patch: Partial<FieldMapping>) => commit(rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));
    return (
        <Stack spacing={1} className="fluss-payload-mapping">
            {rows.map((row, index) => {
                const invalid = Boolean(row.target) && !validateKey(row.target);
                const duplicate = Boolean(row.target) && rows.filter((candidate) => candidate.target === row.target).length > 1;
                return (
                    <Box key={index} className="fluss-payload-row">
                        <TextField
                            size="small"
                            value={row.target}
                            onChange={(event) => updateRow(index, { target: event.target.value })}
                            error={invalid || duplicate}
                            helperText={duplicate ? String(t("fluss.node_panel.factory_action.duplicate_key", { defaultValue: "Duplicate key" })) : undefined}
                            disabled={disabled}
                            inputProps={{ "aria-label": keyLabel }}
                            sx={{ minWidth: 0 }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                            <DataPicker
                                value={row.source}
                                fieldPath={`${fieldPath}.${row.target}`}
                                onChange={(source) => updateRow(index, { source })}
                                expectedType="TEMPLATE_TEXT"
                                disabled={disabled}
                                ariaLabel={valueLabel}
                            />
                        </Box>
                        <IconButton size="small" aria-label={String(t("fluss.node_panel.factory_action.remove_template_data", { defaultValue: "Remove template data" }))} disabled={disabled} onClick={() => commit(rows.filter((_, rowIndex) => rowIndex !== index))}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </Box>
                );
            })}
            <Button size="small" startIcon={<Add />} disabled={disabled} onClick={() => commit([...rows, { target: "", source: undefined }])}>
                {t("fluss.node_panel.factory_action.add", { defaultValue: "Add" })}
            </Button>
        </Stack>
    );
};

const PayloadParamField = ({
    value,
    onChange,
    disabled,
    allowText = true,
    allowScope = false,
}: {
    value: unknown;
    onChange: (value: unknown) => void;
    disabled?: boolean;
    allowText?: boolean;
    allowScope?: boolean;
}) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState<"key_value" | "text" | "scope">(() => {
        if (value === undefined || value === null) return "key_value";
        if (typeof value === "object" && !Array.isArray(value)) return "key_value";
        // ACH-026: schema options decide which scalar mode is valid for this shared payload field.
        if (allowScope && ["string", "number", "boolean"].includes(typeof value)) return "scope";
        return typeof value === "string" ? "text" : "key_value";
    });
    const [rows, setRows] = useState<FieldMapping[]>(() => mappingRows(value));
    const [text, setText] = useState(() => typeof value === "string" ? value : JSON.stringify(value || {}, null, 2));
    const [confirmReset, setConfirmReset] = useState<"key_value" | "scope" | false>(false);
    const keyValueLabel = String(t("fluss.node_panel.service.param.payload.mode.key_value", { defaultValue: "Key/value" }));
    const textLabel = String(t("fluss.node_panel.service.param.payload.mode.text", { defaultValue: "Free text" }));
    const scopeLabel = String(t("fluss.node_panel.service.param.payload.mode.scope", { defaultValue: "Single value" }));
    const hasScopeValue = value !== undefined && value !== null && value !== "";
    const switchToText = () => {
        const next = JSON.stringify(rowsObject(rows), null, 2);
        setText(next);
        setMode("text");
        onChange(next);
    };
    const applyRows = (next: FieldMapping[]) => {
        setRows(next);
        setMode("key_value");
        onChange(rowsObject(next));
    };
    const switchToKeyValue = () => {
        if (mode === "scope") {
            if (hasScopeValue) setConfirmReset("key_value");
            else applyRows([]);
            return;
        }
        const converted = simplePayloadRows(text);
        if (converted.ok) applyRows(converted.rows);
        else setConfirmReset("key_value");
    };
    const switchToScope = () => {
        if (Object.keys(rowsObject(rows)).length > 0) setConfirmReset("scope");
        else {
            setMode("scope");
            onChange(undefined);
        }
    };
    const confirmModeReset = () => {
        if (confirmReset === "scope") {
            setMode("scope");
            onChange(undefined);
        } else {
            applyRows([]);
        }
        setConfirmReset(false);
    };
    return (
        <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
                <Button size="small" variant={mode === "key_value" ? "contained" : "outlined"} disabled={disabled || mode === "key_value"} onClick={switchToKeyValue} aria-pressed={mode === "key_value"}>{keyValueLabel}</Button>
                {allowText && <Button size="small" variant={mode === "text" ? "contained" : "outlined"} disabled={disabled || mode === "text"} onClick={switchToText} aria-pressed={mode === "text"}>{textLabel}</Button>}
                {allowScope && <Button size="small" variant={mode === "scope" ? "contained" : "outlined"} disabled={disabled || mode === "scope"} onClick={switchToScope} aria-pressed={mode === "scope"}>{scopeLabel}</Button>}
            </Stack>
            <Typography variant="caption" color="text.secondary">
                {t("fluss.node_panel.service.param.payload.active_mode", { defaultValue: "Active mode: {{mode}}", mode: mode === "key_value" ? keyValueLabel : mode === "scope" ? scopeLabel : textLabel })}
            </Typography>
            {mode === "text" ? (
                <TemplateTextField value={text} multiline disabled={disabled} label={String(t("fluss.node_panel.service.param.payload", { defaultValue: "Payload" }))} onChange={(next) => { setText(next); onChange(next); }} />
            ) : mode === "scope" ? (
                <DataPicker value={value as FlussValue} onChange={(next) => onChange(next)} literalTemplate disabled={disabled} ariaLabel={String(t("fluss.node_panel.service.param.payload", { defaultValue: "Payload" }))} />
            ) : (
                <FreeKeyScopeMappingField value={rowsObject(rows)} onChange={applyRows} disabled={disabled} keyFormat="json_property" />
            )}
            <Dialog open={Boolean(confirmReset)} onClose={() => setConfirmReset(false)}>
                <DialogTitle>{t("fluss.node_panel.service.param.payload.switch_warning_title", { defaultValue: "Replace payload?" })}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t(confirmReset === "scope" || mode === "scope" ? "fluss.node_panel.service.param.payload.switch_scope_warning_body" : "fluss.node_panel.service.param.payload.switch_warning_body", { defaultValue: "This payload cannot be converted to the selected mode. Switching will clear it." })}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmReset(false)} autoFocus>{t("fluss.node_panel.service.param.payload.switch_cancel", { defaultValue: "Cancel" })}</Button>
                    <Button onClick={confirmModeReset}>{t("fluss.node_panel.service.param.payload.switch_confirm", { defaultValue: "Replace" })}</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export const FactoryActionParamForm = ({
    action,
    params,
    data,
    disabled,
    recordTableName,
    recordSourceField,
    hideDeprecatedTemplateValue = false,
    onChange,
}: {
    action: FlussMetadataOption;
    params: Record<string, any>;
    data: FlussNodePanelData;
    disabled?: boolean;
    recordTableName?: string;
    recordSourceField?: React.ReactNode;
    hideDeprecatedTemplateValue?: boolean;
    onChange: (params: Record<string, any>) => void;
}) => {
    const { t } = useTranslation();
    const schema = useMemo(() => parseFactorySchema(action.params_schema), [action.params_schema]);
    const tableName = String(params.table || "");
    const columnsQuery = useFlussTableColumns(tableName, data.tables);
    const selectedTemplate = data.templates.find((item) => item.id === params.email_template);
    const templateTableName = String(selectedTemplate?.table_name || "");
    const targetFields = useMemo(() => metadataToScopeOptions(columnsQuery.data || []), [columnsQuery.data]);
    useEffect(() => {
        if (action.name !== "update_record" || params.target !== "record") return;
        if ((recordTableName && params.table !== recordTableName) || params.record_id !== undefined) {
            const next: Record<string, any> = { ...params, ...(recordTableName ? { table: recordTableName } : {}) };
            delete next.record_id;
            onChange(next);
        }
    }, [action.name, recordTableName, params, onChange]);
    useEffect(() => {
        if (action.name !== "update_record" || params.target === "record" || !columnsQuery.data || !params.values) return;
        const allowed = new Set(columnsQuery.data.map((column: any) => column.name));
        const values = parseFactoryParams(params.values);
        const compatible = Object.fromEntries(Object.entries(values).filter(([key]) => allowed.has(key)));
        if (Object.keys(compatible).length !== Object.keys(values).length) onChange({ ...params, values: compatible });
    }, [action.name, columnsQuery.data, params, onChange]);
    // ACH-019/ACH-011: mapping writes to creatable columns; the single-field picker keeps all columns.
    const mappingTargetFields = useMemo(() => metadataToCreatableScopeOptions(columnsQuery.data || []), [columnsQuery.data]);
    const derivesRecordClass = action.name === "create_record" && Boolean(tableName)
        && mappingTargetFields.some((field) => field.name === "sys_class_name");
    const visibleMappingFields = derivesRecordClass
        ? mappingTargetFields.filter((field) => field.name !== "sys_class_name") : mappingTargetFields;
    useEffect(() => {
        if (!derivesRecordClass || disabled) return;
        const current = parseFactoryParams(params.values);
        if (current.sys_class_name === tableName) return;
        onChange({ ...params, values: { ...current, sys_class_name: tableName } });
    }, [derivesRecordClass, disabled, tableName, params, onChange]);
    useEffect(() => {
        if (action.name !== "find_record") return;
        const next = { ...params };
        for (const key of ["order_field", "order_direction"]) {
            if (key in next && emptyOrderValue(next[key])) delete next[key];
        }
        if (Object.keys(next).length !== Object.keys(params).length) onChange(next);
    }, [action.name, params, onChange]);
    const setParam = (key: string, value: unknown) => {
        const next = { ...params };
        if (action.name === "find_record" && ["order_field", "order_direction"].includes(key) && emptyOrderValue(value)) delete next[key];
        else next[key] = value;
        if (action.name === "update_record" && key === "target" && value === "record") {
            delete next.record_id;
            if (recordTableName) next.table = recordTableName;
        }
        onChange(next);
    };
    const setTableParam = (key: string, value: string) => {
        const nextParams = { ...params, [key]: value };
        // Defensive: MUI's Autocomplete already suppresses onChange when the clicked
        // option is referentially the same as the current `value` (see
        // @mui/base/AutocompleteUnstyled/useAutocomplete.js, `if (value === newValue) return`).
        // `value` here is derived via `data.tables.find(...)` from the SAME array that
        // feeds `options` in the same render, so they're always the same object -- this
        // branch is not reachable through today's UI path regardless of whether
        // `data.tables` itself is a stable or freshly-created prop. Kept as a guard
        // against a same-value call reaching setTableParam through some other caller.
        if (value !== params[key]) {
            schema.forEach((candidate) => {
                if (candidate.options?.table_param === key) {
                    if (action.name === "find_record" && candidate.key === "order_field") delete nextParams[candidate.key];
                    else if (!(action.name === "update_record" && candidate.key === "values")) nextParams[candidate.key] = {};
                }
            });
        }
        onChange(nextParams);
    };
    const label = (field: FactoryParamSchema) => String(t(
        field.label || `fluss.node_panel.service.param.${field.key}`,
        { defaultValue: field.label || field.key.replaceAll("_", " ") }
    ));
    const isFieldVisible = (field: FactoryParamSchema) => {
        // ACH-023/PRIM-PILOT-036: mirror factory_action_binding.rb update-only guard locally;
        // generic visible_when belongs to PRIM-PILOT-035.
        if (action.name === "dispatch_form" && field.key === "record_id" && params.mode !== "update") return false;
        if (action.name === "update_record" && field.key === "record_id" && params.target === "record") return false;
        return true;
    };
    const updateRecordTableMismatch = action.name === "update_record" && params.target === "record" && params.table && recordTableName && params.table !== recordTableName;

    return (
        <Stack spacing={1.25} data-testid="factory-action-params">
            {action.name === "dispatch_form" && (
                // PRIM-PILOT-037: dispatch_form is send-and-continue. form_dispatch_id is a
                // Service Task output for tracking/mapping, not a submission correlation key.
                <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                        {t("fluss.node_panel.service.action.dispatch_form.wait_hint", {
                            defaultValue:
                                "This node dispatches the form and continues. form_dispatch_id is an output of this node that can be mapped in Output variables for tracking; it is not sent to the form or saved on submit, so do not use it as a correlation key.",
                        })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                        {t("fluss.node_panel.service.action.dispatch_form.correlation_hint", {
                            defaultValue:
                                "To wait, chain a Signal Catch correlated by a value visible on both sides, such as record.id in update mode, and emit that signal from a Business Rule on the form table using Emit event.",
                        })}
                    </Typography>
                </Stack>
            )}
            {action.name === "update_record" && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                    {t("fluss.node_panel.service.action.update_record.target_hint_clear", {
                        defaultValue: "O destino define qual registro será atualizado. A tabela define onde a gravação ocorre; para um registro relacionado, o ID precisa pertencer a ela.",
                    })}
                </Typography>
            )}
            {updateRecordTableMismatch && (
                <Alert severity="warning">
                    {t("fluss.node_panel.service.action.update_record.table_mismatch", {
                        defaultValue: "Publishing will be blocked because Source record points to {{recordTableName}}, but Table is {{tableName}}.",
                        recordTableName,
                        tableName: params.table,
                    })}
                </Alert>
            )}
            {schema.filter(isFieldVisible).map((field) => {
                const required = Boolean(field.required || (field.options?.required_when_template_table && templateTableName));
                const title = `${label(field)}${required ? " *" : ""}`;
                const value = params[field.key];
                let control: React.ReactNode;
                if (hideDeprecatedTemplateValue && action.name === "send_email" && field.key === "data_value") return null;
                if (action.name === "update_record" && field.key === "table" && params.target === "record") return null;
                // The outputs of Find record are fixed by the contract (found, record, count): the
                // node panel lists them read-only under "Saídas", there is nothing to choose.
                if (action.name === "find_record" && field.key === "fields") return null;
                if (field.input_type === "table") {
                    control = <Autocomplete size="small" options={sortedTableOptions(data.tables, (item) => String(t(String(item.label)) || item.name))} renderOption={(props, item) => renderTableOption(props, item, String(t(String(item.label)) || item.name))} value={data.tables.find((item) => item.name === value) || (typeof value === "string" && value ? { id: value, name: value, label: value } : null)} isOptionEqualToValue={(option, selected) => option.name === selected.name} getOptionLabel={(item) => t(String(item.label)) || item.name} onChange={(_, item) => setTableParam(field.key, item?.name || "")} renderInput={(input) => <TextField {...input} inputProps={{ ...input.inputProps, "aria-label": title }} />} disabled={disabled} />;
                } else if (field.input_type === "field" && field.options?.multiple) {
                    const selected = Array.isArray(value) ? value : [];
                    control = <Autocomplete multiple size="small" options={targetFields} value={targetFields.filter((item) => selected.includes(item.name))} getOptionLabel={(item) => action.name === "find_record" && field.key === "fields" ? findRecordFieldLabel(t, item) : item.label} onChange={(_, items) => setParam(field.key, items.map((item) => item.name))} renderInput={(input) => <TextField {...input} inputProps={{ ...input.inputProps, "aria-label": title }} />} disabled={disabled} />;
                } else if (field.input_type === "field") {
                    control = <TextField select size="small" value={typeof value === "string" ? value : ""} onChange={(event) => setParam(field.key, event.target.value)} disabled={disabled} inputProps={{ "aria-label": title }}><MenuItem value=""><em>—</em></MenuItem>{targetFields.map((item) => <MenuItem key={item.name} value={item.name}>{action.name === "find_record" ? findRecordFieldLabel(t, item) : item.label}</MenuItem>)}</TextField>;
                } else if (field.input_type === "scope" && field.options?.multiple && field.options?.free_keys && (field.options?.free_text || field.options?.single_scope)) {
                    control = <PayloadParamField key={`${action.name}:${field.key}`} value={value} onChange={(next) => setParam(field.key, next)} disabled={disabled} allowText={Boolean(field.options?.free_text)} allowScope={Boolean(field.options?.single_scope)} />;
                } else if (field.input_type === "scope" && field.options?.multiple && field.options?.free_keys) {
                    control = <FreeKeyScopeMappingField value={value} onChange={(rows) => setParam(field.key, rowsObject(rows))} disabled={disabled} keyFormat={field.options?.key_format === "http_header" ? "http_header" : field.options?.key_format === "json_property" ? "json_property" : undefined} />;
                } else if (field.input_type === "scope" && field.options?.multiple) {
                    const classDerived = derivesRecordClass && field.key === "values";
                    control = <ScopeMappingField value={classDerived ? Object.fromEntries(Object.entries(parseFactoryParams(value)).filter(([key]) => key !== "sys_class_name")) : value} onChange={(rows) => setParam(field.key, { ...rowsObject(rows), ...(classDerived ? { sys_class_name: tableName } : {}) })} targetFields={classDerived ? visibleMappingFields : mappingTargetFields} disabled={disabled} enforceRequired={action.name !== "update_record" && field.enforce_required !== false} templateText={field.key === "values" && ["create_record", "update_record"].includes(action.name)} />;
                } else if (field.input_type === "scope") {
                    control = <DataPicker value={value as FlussValue} onChange={(next) => setParam(field.key, next)} disabled={disabled} ariaLabel={title} />;
                } else if (field.input_type === "condition" && field.options?.table_param) {
                    control = <FindRecordFilterField value={value} onChange={(next) => setParam(field.key, next)} fields={targetFields} disabled={disabled} />;
                } else if (field.input_type === "condition") {
                    control = <FlussConditionBuilder value={value} onChange={(next) => setParam(field.key, next)} />;
                } else if (field.input_type === "duration") {
                    control = <DurationInput value={(value || { amount: null, unit: "minutes" }) as DurationValue} onChange={(next) => setParam(field.key, next)} disabled={disabled} allowBusinessHours />;
                } else if (field.input_type === "event_type") {
                    control = <EventTypeSelect value={value || null} onChange={(id) => setParam(field.key, data.eventTypes.find((item) => item.id === id)?.name || "")} options={data.eventTypes.map((item) => ({ id: item.id, name: item.name, label: item.label || item.name, active: item.active !== false }))} disabled={disabled} />;
                } else if (field.input_type === "email_template") {
                    control = <Autocomplete size="small" options={data.templates.filter((item) => item.active !== false)} value={data.templates.find((item) => item.id === value) || null} getOptionLabel={(item) => item.label || item.name} onOpen={() => { void data.reloadTemplates?.(); }} onChange={(_, item) => setParam(field.key, item?.id || "")} renderInput={(input) => <TextField {...input} inputProps={{ ...input.inputProps, "aria-label": title }} />} disabled={disabled} />;
                } else if (field.input_type === "user_group") {
                    control = <QueueSelect
                        value={queueSelectionFromFactoryUserGroup(value)}
                        onChange={(next) => setParam(field.key, factoryUserGroupFromQueueSelection(next))}
                        roles={data.roles}
                        groups={data.groups}
                        groupRoles={data.groupRoles}
                        disabled={disabled}
                    />;
                } else if (field.input_type === "select") {
                    const options = field.options?.catalog === "sys_api_secrets"
                        ? data.apiSecrets.filter((item) => item.active !== false).map((item) => ({ value: item.name, label: item.label || item.name }))
                        : field.options?.catalog === "sys_sp_form_catalog"
                        ? data.serviceForms.filter((item) => item.active !== false && item.published !== false).map((item) => ({
                            value: item.id,
                            // sys_sp_form_catalog's display field is `title` (a TRANSLATABLE_TEXT
                            // key), not `label` -- same lookup StartNodePanel's own form picker
                            // already uses (its `formId` Autocomplete's getOptionLabel).
                            label: String(t(item.title || item.name, { defaultValue: item.title || item.name })),
                        }))
                        : (field.options?.values || []).map((item: string) => ({ value: item, label: String(t(`fluss.node_panel.service.param.${field.key}.${item}`, { defaultValue: item })) }));
                    control = <TextField select size="small" value={typeof value === "string" ? value : ""} onChange={(event) => setParam(field.key, event.target.value)} disabled={disabled} inputProps={{ "aria-label": title }}><MenuItem value=""><em>—</em></MenuItem>{options.map((item: any) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}</TextField>;
                } else if (action.name === "call_api" && field.key === "url") {
                    control = <TemplateTextField value={String(value || "")} onChange={(next) => setParam(field.key, next)} disabled={disabled} label={title} />;
                } else {
                    control = <TextField size="small" value={typeof value === "object" ? JSON.stringify(value) : value || ""} multiline={Boolean(field.options?.multiline)} minRows={field.options?.multiline ? 3 : undefined} onChange={(event) => setParam(field.key, event.target.value)} disabled={disabled} inputProps={{ "aria-label": title }} />;
                }
                const tooltip = action.name === "emit_event" && field.key === "correlation_key"
                    ? t("fluss.node_panel.service.param.correlation_key.hint", {
                        defaultValue:
                            "Optional. Fill it in only if some Signal Catch elsewhere should resume when this event fires — it must match that node's correlation key and event type exactly. Left blank, the event is still published normally, but no waiting process can be resumed by it.",
                    })
                    : action.name === "call_api" && field.key === "headers"
                        ? t("fluss.node_panel.service.param.headers.hint", {
                            defaultValue: "Use {{secret}} in Authorization, API key, token, or secret headers; plaintext credentials are blocked.",
                        })
                        : undefined;
                const fieldKey = field.options?.table_param ? `${field.key}:${String(params[field.options.table_param] || "")}` : field.key;
                return <Box key={fieldKey}>
                    <FlussNodePanelField label={title} tooltip={tooltip} fieldPath={`params.${field.key}`}>{control}</FlussNodePanelField>
                    {action.name === "update_record" && field.key === "target" && params.target === "record" && recordSourceField}
                </Box>;
            })}
        </Stack>
    );
};
